import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { clientApi, publicApi } from '@/config/axios';

export function SuperbillActions({
  base,
  token,
  canGenerate = false,
}: {
  base: string;
  token?: string;
  canGenerate?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const api = token ? publicApi : clientApi;
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  async function act(generate: boolean) {
    setBusy(true);
    try {
      if (generate) await api.post(`${base}/superbill`, {}, config);
      const response = await api.get(`${base}/superbill/status`, config);
      const status = response.data.status;
      setReady(status === 'completed');
      setMessage(
        status === 'completed'
          ? 'Your superbill is ready.'
          : status === 'not_requested'
            ? 'The practice has not requested a Jasper superbill yet.'
            : `Generation status: ${String(status).replaceAll('_', ' ')}. Check again shortly, or contact the practice if generation failed.`,
      );
    } catch {
      setMessage('Jasper generation is unavailable or not configured. The itemized claim packet is still available.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label="Jasper superbill" className="flex flex-col gap-3">
      <h3 className="font-medium">Superbill</h3>
      <p>Generate a separate itemized superbill from the reviewed billing record.</p>
      <div className="flex flex-wrap gap-3">
        {!token && canGenerate && (
          <Button disabled={busy} variant="outline" onClick={() => act(true)}>
            Generate superbill (Jasper)
          </Button>
        )}
        <Button disabled={busy} variant="outline" onClick={() => act(false)}>
          Check superbill status
        </Button>
        {ready && (
          <Button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const response = await api.get(`${base}/superbill`, { ...config, responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'superbill.pdf';
                link.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch {
                setMessage('Unable to download the superbill. Check its status again.');
              } finally {
                setBusy(false);
              }
            }}>
            Download superbill
          </Button>
        )}
      </div>
      {message && <p role="status">{message}</p>}
    </section>
  );
}
